import { motion } from "framer-motion";
import { FaArrowRight, FaChevronDown, FaJava } from "react-icons/fa";
import {
  SiCss,
  SiDocker,
  SiGit,
  SiHtml5,
  SiJavascript,
  SiMongodb,
  SiNodedotjs,
  SiPostgresql,
  SiPython,
  SiReact,
} from "react-icons/si";
import { TbBrandCSharp } from "react-icons/tb";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locales/translations";
import "./Home.css";

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1 },
  out: { opacity: 0 },
};

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
};

const skills = [
  { name: "Java", icon: FaJava, level: 75, color: "#ED8B00" },
  { name: "JavaScript", icon: SiJavascript, level: 67, color: "#F7DF1E" },
  { name: "React", icon: SiReact, level: 72, color: "#61DAFB" },
  { name: "HTML", icon: SiHtml5, level: 92, color: "#E34F26" },
  { name: "CSS", icon: SiCss, level: 76, color: "#1572B6" },
  { name: "Node.js", icon: SiNodedotjs, level: 63, color: "#339933" },
  { name: "Python", icon: SiPython, level: 68, color: "#3776AB" },
  { name: "PostgreSQL", icon: SiPostgresql, level: 62, color: "#4169E1" },
  { name: "MongoDB", icon: SiMongodb, level: 70, color: "#47A248" },
  { name: "Git", icon: SiGit, level: 68, color: "#F05032" },
  { name: "Docker", icon: SiDocker, level: 42, color: "#2496ED" },
  { name: "C#", icon: TbBrandCSharp, level: 72, color: "#9B4F96" },
];

const SkillCard = ({ skill, index, lang, t }) => {
  const getLevel = (level) => {
    if (level >= 85) return t.skills.level_expert;
    if (level >= 70) return t.skills.level_advanced;
    if (level >= 55) return t.skills.level_intermediate;
    return t.skills.level_beginner;
  };

  return (
    <motion.div
      className="skill-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -6, boxShadow: "0 20px 50px rgba(99, 102, 241, 0.18)" }}
    >
      <div className="skill-card__icon" style={{ color: skill.color }}>
        <skill.icon size={38} />
      </div>
      <p className="skill-card__name">{skill.name}</p>
      <div className="skill-card__bar">
        <motion.div
          className="skill-card__bar-fill"
          initial={{ width: 0 }}
          whileInView={{ width: `${skill.level}%` }}
          viewport={{ once: true }}
          transition={{
            duration: 1.2,
            delay: index * 0.06 + 0.3,
            ease: "easeOut",
          }}
        />
      </div>
      <span className="skill-card__level">{getLevel(skill.level)}</span>
    </motion.div>
  );
};

const Home = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="in"
      exit="out"
      transition={{ duration: 0.4 }}
    >
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__blob hero__blob--1" />
        <div className="hero__blob hero__blob--2" />

        <div className="hero__container">
          <div className="hero__content">
            <motion.p
              className="hero__greeting"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t.hero.greeting}
            </motion.p>

            <motion.h1
              className="hero__name"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
            >
              Benjamin <span className="hero__name--gradient">REPELLIN</span>
            </motion.h1>

            <motion.div
              className="hero__role-wrapper"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <span className="hero__role">{t.hero.role}</span>
            </motion.div>

            <motion.p
              className="hero__description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
            >
              {t.hero.description}
            </motion.p>

            <motion.div
              className="hero__cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Link to="/projects" className="btn btn--primary">
                {t.hero.cta_projects}
                <FaArrowRight size={14} />
              </Link>
              <Link to="/contact" className="btn btn--outline">
                {t.hero.cta_contact}
              </Link>
            </motion.div>
          </div>

          <motion.div
            className="hero__photo-wrapper"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.7,
              delay: 0.4,
              type: "spring",
              stiffness: 80,
            }}
          >
            <div className="hero__photo-ring" />
            <div className="hero__photo">
              <img
                src="/picture-of-me.jpg"
                alt="Benjamin REPELLIN"
                className="hero__photo-img"
              />
            </div>
            <div className="hero__photo-dot hero__photo-dot--1" />
            <div className="hero__photo-dot hero__photo-dot--2" />
            <div className="hero__photo-dot hero__photo-dot--3" />
          </motion.div>
        </div>

        <div className="hero__scroll-hint">
          <motion.button
            className="hero__scroll-btn"
            onClick={() =>
              document
                .getElementById("about")
                .scrollIntoView({ behavior: "smooth" })
            }
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            aria-label="Scroll to about"
          >
            <span className="hero__scroll-btn-label">
              {lang === "fr" ? "À propos" : "About me"}
            </span>
            <div className="hero__scroll-btn-circle">
              <FaChevronDown size={14} />
            </div>
          </motion.button>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="about">
        <div className="section__container">
          <motion.div
            className="section__header"
            {...fadeUp}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section__tag">
              {lang === "fr" ? "Qui suis-je ?" : "Who am I?"}
            </span>
            <h2 className="section__title">{t.about.title}</h2>
          </motion.div>

          <div className="about__grid">
            <motion.div
              className="about__text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <p>{t.about.text1}</p>
              <p>{t.about.text2}</p>
            </motion.div>

            <div className="about__stats">
              {[
                { value: t.about.stat1_value, label: t.about.stat1_label },
                { value: t.about.stat2_value, label: t.about.stat2_label },
                { value: t.about.stat3_value, label: t.about.stat3_label },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="about__stat"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  whileHover={{ y: -4 }}
                >
                  <span className="about__stat-value">{stat.value}</span>
                  <span className="about__stat-label">{stat.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SKILLS ── */}
      <section className="skills">
        <div className="section__container">
          <motion.div
            className="section__header"
            {...fadeUp}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section__tag">
              {lang === "fr" ? "Ce que je sais faire" : "What I can do"}
            </span>
            <h2 className="section__title">{t.skills.title}</h2>
            <p className="section__subtitle">{t.skills.subtitle}</p>
          </motion.div>

          <div className="skills__grid">
            {skills.map((skill, i) => (
              <SkillCard
                key={skill.name}
                skill={skill}
                index={i}
                lang={lang}
                t={t}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer__container">
          <p>
            © {new Date().getFullYear()} Benjamin REPELLIN —{" "}
            {t.footer.made_with} React &nbsp;|&nbsp; {t.footer.rights}
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default Home;
